import {
  Box,
  Step,
  StepDescription,
  StepIcon,
  StepIndicator,
  StepNumber,
  StepSeparator,
  StepTitle,
  Stepper,
  StepStatus,
  Flex,
  Spinner,
  Text,
  useBreakpointValue,
  Show,
} from "@chakra-ui/react";
import BookingSummaryStep from "./steps/BookingSummaryStep";
import BookingConfirmationStep from "./steps/BookingConfirmationStep";
import UserDataStep from "./steps/UserDataStep";
import { useMatch, useSearchParams } from "react-router-dom";
import { getBooking } from "../../services/bookings";
import { useQuery } from "@tanstack/react-query";
import { ACCOMMODATION_TYPES } from "../../constants";
import {getForeignPartnerBooking} from "../../services/affiliate";
import {useState, useEffect, useCallback} from "react";
import { useTranslation } from "react-i18next";
import { useAbandonDraftOnLeave } from "../../hooks/useAbandonDraftOnLeave";
import { useAuth } from "../../contexts/AuthContext";
import { ROLES } from "../../constants";
import { bookingHasRequiredDocuments } from "../../utils/helpers";

const DETAILS_STEP_INDEX = 1;
const CHECKOUT_STEP_INDEX = 2;

const STEP_NAME_TO_INDEX = {
  details: DETAILS_STEP_INDEX,
  checkout: CHECKOUT_STEP_INDEX,
};

const STEP_INDEX_TO_NAME = {
  [DETAILS_STEP_INDEX]: "details",
  [CHECKOUT_STEP_INDEX]: "checkout",
};

// The checkout step is only valid once the booking actually has the data it
// depends on: client contact details plus the required passport and ticket
// photos. This keeps the displayed step honest after a reload or a deep link.
const canEnterCheckout = (booking) => {
  if (!booking) return false;

  if (booking.isAffiliateReservation) {
    return bookingHasRequiredDocuments(booking);
  }

  const hasContact = Boolean(
    booking.clientFirstName &&
      booking.clientEmail &&
      booking.clientNameOnPassport
  );

  return hasContact && bookingHasRequiredDocuments(booking);
};

function StepsLayout({ nextStep, prevStep, activeStep, children, isAffiliateCheckout }) {
  return (
    <Flex
      justifyContent="space-between"
      direction={["column", "column", "row"]}
    >
      <Box pt="24px" pr={[0, 0, "12px"]} flex="1 1 0">
        {children[0]}
      </Box>
      {activeStep === 1 && (
        <Box pt="24px" pl={[0, 0, "12px"]} flex="2 1 0">
          {children[1]}
        </Box>
      )}
      {activeStep === 2 && !isAffiliateCheckout && (
        <Box pt="24px" pl={[0, 0, "12px"]} flex="2 1 0">
          {children[2]}
        </Box>
      )}
    </Flex>
  );
}

function BookingStepper() {
  const { t } = useTranslation("common");
  const { currentUser } = useAuth();
  const match = useMatch("/book/:bookingId");
  const bookingId = match?.params.bookingId;
  const [isForeignPartnerReservation, setIsForeignPartnerReservation] = useState(false);

  const { data: foreignPartnerBooking } = useQuery(
      ["get-foreign-partner-booking", bookingId],
      () => getForeignPartnerBooking(bookingId),
      {
        select: (data) => data.data,
        onSuccess: (data) => {
          if (data.error) {
            setIsForeignPartnerReservation(false);
            return;
          }
          if (data.booking?.uuid === bookingId) {
            setIsForeignPartnerReservation(true);
          } else {
            setIsForeignPartnerReservation(false);
          }
        },
        onError: (error) => {
          setIsForeignPartnerReservation(false);
        },
        refetchOnWindowFocus: false,
        refetchOnMount: false,
        refetchOnReconnect: false,
        retry: false,
      }
  );

  const { isLoading, isError, data } = useQuery(
      ["booking", bookingId],
      () => getBooking(bookingId),
      {
        enabled: !!bookingId,
        select: (data) => data.data,
        retry: (failureCount, error) => {
          const status = error?.response?.status;
          if (status === 401 || status === 404) return false;
          return failureCount < 2;
        },
        refetchOnWindowFocus: true,
        refetchOnReconnect: true,
        refetchOnMount: false,
      }
  );

  const bookingTypeDescription = data
    ? data.bookingType === "stay"
      ? t("bookingStepAccommodation")
      : data.bookingType === "car"
      ? t("bookingStepCar")
      : t("bookingStepTour")
    : t("bookingStepBooking");

  const steps = [
    {
      title: t("bookingStepYourChoice"),
      description: bookingTypeDescription,
    },
    { title: t("bookingStepYourData"), description: t("bookingStepContactInfo") },
    { title: t("bookingStepLastStep"), description: t("bookingStepCompleteReservation") },
  ];

  const [searchParams, setSearchParams] = useSearchParams();

  const setStep = useCallback(
    (index, { replace = false } = {}) => {
      const name = STEP_INDEX_TO_NAME[index];
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (name) {
            next.set("step", name);
          } else {
            next.delete("step");
          }
          return next;
        },
        { replace }
      );
    },
    [setSearchParams]
  );

  const requestedStepName = searchParams.get("step");
  const requestedStepIndex =
    STEP_NAME_TO_INDEX[requestedStepName] ?? DETAILS_STEP_INDEX;

  const isCheckoutAllowed = canEnterCheckout(data);

  const activeStep =
    requestedStepIndex === CHECKOUT_STEP_INDEX && !isCheckoutAllowed
      ? DETAILS_STEP_INDEX
      : requestedStepIndex;

  // Keep the URL honest: if someone deep-links/reloads into checkout without the
  // prerequisites, drop them back to the details step in the URL too.
  useEffect(() => {
    if (
      requestedStepName === "checkout" &&
      data &&
      activeStep === DETAILS_STEP_INDEX
    ) {
      setStep(DETAILS_STEP_INDEX, { replace: true });
    }
  }, [requestedStepName, data, activeStep, setStep]);

  const goToNext = useCallback(
    () => setStep(Math.min(activeStep + 1, CHECKOUT_STEP_INDEX)),
    [activeStep, setStep]
  );
  const goToPrevious = useCallback(
    () => setStep(Math.max(activeStep - 1, DETAILS_STEP_INDEX)),
    [activeStep, setStep]
  );

  const stepsProps = {
    nextStep: goToNext,
    prevStep: goToPrevious,
    activeStep,
  };

  const stepStatusProps = useBreakpointValue(
    {
      md: {
        incomplete: <StepNumber />,
        active: <StepNumber />,
      },
      base: {},
    },
    {
      fallback: "md",
    }
  );

  const { abandonDraft } = useAbandonDraftOnLeave(
    bookingId,
    data?.bookingType,
    data?.currentStatus,
    isForeignPartnerReservation
  );

  if (isLoading) return <Spinner />;
  if (isError || !data) return null;

  const isAffiliateCheckout =
    data?.isAffiliateReservation || currentUser?.type === ROLES.FOREIGN_PARTNER;

  let bookingSummaryProps = {};

  // FOR STAY BOOKING TYPE
  if (data?.bookingType === "stay") {
    bookingSummaryProps = {
      accommodation: data?.accommodation,
      details: {
        checkIn: new Date(data?.checkInDate),
        checkOut: new Date(data?.checkOutDate),
        totalNights: data?.totalStayLengthInDays,
        adults: data?.adults,
        children: data?.children,
        priceSummary: {
          entries: data?.accommodationUnits.map((unit) => ({
            name: unit.name,
            startDate: unit.startDate,
            endDate: unit.endDate,
            totalPricePerUnit: unit.totalPrice,
          })),
          totalPrice: data?.fullPrice,
          //discountPrice i promoCode nisam pass-ao dalje jer ih fetch-am u kasnijim komponentama i zovem getBooking
        },
      },
      accommodationUnits: data?.accommodationUnits,
    };
  }
  // FOR CAR BOOKING TYPE
  else if (data?.bookingType === "car") {
    bookingSummaryProps = {
      car: data?.car,
      details: {
        checkIn: new Date(data?.checkInDate),
        checkOut: new Date(data?.checkOutDate),
        pickupTime: data?.pickupTime,
        dropoffTime: data?.dropoffTime,
        totalNights: data?.totalStayLengthInDays,
        adults: data?.adults,
        children: data?.children,
        priceSummary: {
          entries: [],
          totalPrice: data?.fullPrice,
        },
      },
    };
  }
  // FOR TOUR BOOKING TYPE
  else if (data?.bookingType === "tour") {
    bookingSummaryProps = {
      tour: data?.tour,
      details: {
        checkIn: new Date(data?.checkInDate),
        checkOut: new Date(data?.checkOutDate),
        totalNights: data?.totalStayLengthInDays,
        adults: data?.adults,
        children: data?.children,
        priceSummary: {
          entries: data?.tour.accommodationUnits.map((unit) => ({
            //name: unit.stay.name,
            name:
              unit.stay.type === ACCOMMODATION_TYPES.HOTEL
                ? `${unit.name} (${unit.stay.name})`
                : `${unit.name}`,
            startDate: unit.startDate,
            endDate: unit.endDate,
            totalPricePerUnit: unit.totalPrice,
          })),
          //tax: 5,
          vehicleCategory: data?.tour.selectedVehicleCategory,
          //driverPrice: data?.driverPrice,
          pickupLocation: data?.tour.pickupLocation,
          totalPrice: data?.fullPrice,
        },
      },
      accommodationUnits: data?.tour.accommodationUnits,
    };
  }

  bookingSummaryProps["bookingType"] = data?.bookingType;
  bookingSummaryProps["isForeignPartnerReservation"] = isForeignPartnerReservation;

  const stepsComponents = [
    <BookingSummaryStep
      key="booking-summary"
      {...bookingSummaryProps}
      abandonDraft={abandonDraft}
    />,
    <UserDataStep
      key="user-data"
      {...stepsProps}
      startDate={bookingSummaryProps.details.checkIn}
      endDate={bookingSummaryProps.details.checkOut}
      bookingId={data?.id}
      booking={data}
      isForeignPartnerReservation={isForeignPartnerReservation}
      foreignPartnerBookingClientData={foreignPartnerBooking}
    />,
    <BookingConfirmationStep
      key="booking-confirmation"
      {...stepsProps}
      totalPrice={data?.fullPrice}
      isAffiliateCheckout={isAffiliateCheckout}
    />,
  ];

  return (
          <>
            <Stepper index={activeStep} orientation="horizontal" gap="0">
              {steps.map((step, index) => (
                  <Step key={index}>
                    <StepIndicator>
                      <StepStatus complete={<StepIcon />} {...stepStatusProps} />
                    </StepIndicator>
                    <Show above="md">
                      <Box flexShrink="0">
                        <StepTitle>{step.title}</StepTitle>
                        <StepDescription>{step.description}</StepDescription>
                      </Box>
                    </Show>
                    <StepSeparator />
                  </Step>
              ))}
            </Stepper>

            <Show below="md">
              <Text mt={2}>
                {t("bookingStepNumber", { step: activeStep + 1 })}{" "}
                <b>{steps[activeStep]?.description}</b>
              </Text>
            </Show>

            <StepsLayout {...stepsProps} isAffiliateCheckout={isAffiliateCheckout}>
              {stepsComponents}
            </StepsLayout>
          </>
  );

}

export default BookingStepper;
